import type { CSSProperties } from "react";
import { Btn, CONTACT, Eyebrow, Item, Page, TK } from "../core";

export default function Trust() {
  return (
    <Page current="/trust" contact={{ title: <>Send us your <em>security questionnaire.</em></>, lede: "We answer vendor security reviews directly, with the person who designed the controls on the call. Tell us your process and your deadline." }}>
      <section className="tk-hero tk-hero--navy">
        <div className="tk-wrap"><div className="tk-stack tk-stack--lg" style={{ maxWidth: 860 }}>
          <div>
            <Eyebrow>Trust and security</Eyebrow>
            <h1 className="tk-h1">Hospitality runs on trust. <em>So we say exactly where we stand.</em></h1>
          </div>
          <p className="tk-lede">thatKorner was founded by a hotel-group CISO and built to pass the vendor security review he used to run. This page states what is in place today, what is in progress, and the date each remaining item is due.</p>
          <div className="tk-row">
            <Btn to={CONTACT}>Request our security pack</Btn>
            <Btn to={TK.legal.dpa} kind="ghost">Read the data processing agreement</Btn>
          </div>
        </div></div>
      </section>

      <section className="tk-sec tk-sec--navy" style={{ paddingTop: 0 }}>
        <div className="tk-wrap">
          <div className="tk-tablewrap">
            <table className="tk-table">
              <thead><tr><th scope="col">Item</th><th scope="col">Status</th><th scope="col">Detail</th></tr></thead>
              <tbody>
                <tr><td>ISO 27001</td><td><span className="tk-status">In progress</span></td><td>Implementation under way. <b>Stage 2 audit planned for Q4 2026.</b> We do not claim certification until the certificate is issued.</td></tr>
                <tr><td>SOC 2 Type II</td><td><span className="tk-status">Scheduled</span></td><td><b>Scheduled for Q1 2027.</b> Not yet attested.</td></tr>
                <tr><td>Data protection law</td><td><span className="tk-status tk-status--live">In place</span></td><td>Processing designed for UAE PDPL, GDPR and India's DPDP Act 2023. A data processing agreement is signed before any data is processed.</td></tr>
                <tr><td>Data residency</td><td><span className="tk-status tk-status--live">In place</span></td><td>Hosted on AWS in the UAE region for Gulf customers and in the Mumbai region for customers in India.</td></tr>
                <tr><td>Tenant isolation</td><td><span className="tk-status tk-status--live">In place</span></td><td>Each operator's data is stored separately, encrypted in transit and at rest, and never pooled with another operator's.</td></tr>
                <tr><td>Availability</td><td><span className="tk-status tk-status--live">Contractual</span></td><td>99.9% uptime commitment in enterprise agreements.</td></tr>
                <tr><td>Patent</td><td><span className="tk-status">Pending</span></td><td>Patent application filed. Not yet granted.</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--bone">
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>Where the AI may speak, and where it may not</Eyebrow>
            <h2 className="tk-h2">Language is generated. <em>Facts are looked up.</em></h2>
            <p className="tk-lede">The most important security decision in an AI product is what the model is not allowed to decide. In thatKorner, anything that can harm a guest or mislead a finance director is kept away from it.</p>
          </div>
          <div className="tk-grid tk-grid--4">
            <Item icon="shield" title="Allergens">Answered from a matrix your kitchen signs, dish by dish. The model cannot override it. Where the matrix has no entry, the answer is a refusal and a person is called.</Item>
            <Item icon="chart" title="Numbers">The model plans the question. A deterministic compiler writes the query, and the figure is computed from your data. The plan behind each answer can be inspected.</Item>
            <Item icon="heart" title="Guest preferences">Chosen from a fixed list and saved with consent. There is no free-text field in which a remark about a guest could be stored.</Item>
            <Item icon="chat" title="Conversation">Recommendations, dish stories and pairing language are generated, and grounded in your own menu and your chef's notes.</Item>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--paper" style={{ "--bg": "var(--paper)" } as CSSProperties}>
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>What happens to a request</Eyebrow>
            <h2 className="tk-h2">Five steps, <em>each one logged.</em></h2>
          </div>
          <ol className="tk-flow" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", rowGap: 30 }}>
            <li><b>Authenticate</b><span>Every request is tied to a signed-in user and to one operator's workspace.</span></li>
            <li><b>Encrypt</b><span>Data is encrypted on its way to us and where it is stored.</span></li>
            <li><b>Process</b><span>The request runs inside that operator's scope and cannot read another's data.</span></li>
            <li><b>Record</b><span>Who did what, and when, is written to an audit log the operator can review.</span></li>
            <li><b>Forget</b><span>A guest's request to be forgotten removes their stored preferences.</span></li>
          </ol>
        </div>
      </section>

      <section className="tk-sec tk-sec--ink">
        <div className="tk-wrap tk-split tk-split--top">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Access and accountability</Eyebrow>
              <h2 className="tk-h2">Your team sees what the job needs. <em>Nothing more.</em></h2>
            </div>
            <p className="tk-body">Permissions follow the role. A server sees a guest's allergies. A manager sees spend. A director sees the estate. Changes to menus, prices and permissions are recorded against the person who made them.</p>
          </div>
          <div className="tk-stack">
            <Item icon="key" title="Role-based access">Set per outlet and per role, with sensitive fields withheld from devices that should not hold them.</Item>
            <Item icon="file" title="Audit log">A record of who changed what, and when, across menus, prices, staff and settings.</Item>
            <Item icon="lock" title="Your data stays yours">It is exportable, deletable on request, and never pooled with another operator's for any purpose.</Item>
            <Item icon="plug" title="Read before write">In the first phase of an integration we read from your point-of-sale and write nothing back to it.</Item>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--bone tk-sec--tight">
        <div className="tk-wrap tk-split tk-split--top">
          <div>
            <Eyebrow>Documents</Eyebrow>
            <h2 className="tk-h2">Read them <em>before you sign.</em></h2>
          </div>
          <div className="tk-faq">
            <details open><summary>Policies and agreements</summary><p><a href={TK.legal.privacy}>Privacy notice</a> · <a href={TK.legal.terms}>Terms of service</a> · <a href={TK.legal.cookies}>Cookie policy</a> · <a href={TK.legal.dpa}>Data processing agreement</a></p></details>
            <details><summary>Reporting a security concern</summary><p>Write to {TK.email} with "Security" in the subject line. We acknowledge every report and tell you what we did about it.</p></details>
            <details><summary>Vendor security reviews</summary><p>We complete security questionnaires for hotel groups directly and provide our current control documentation under a non-disclosure agreement.</p></details>
          </div>
        </div>
      </section>
    </Page>
  );
}
